import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";

const Auth = lazy(() => import("./pages/Auth"));
const CustomerPortal = lazy(() => import("./pages/customer/CustomerPortal"));
const AdminWrapper = lazy(() => import("./pages/admin/AdminWrapper"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FarmHerd360Landing = lazy(() => import("./pages/farmherd360/Index"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const GoatFarmingPakistanPage = lazy(
  () => import("./pages/guides/GoatFarmingPakistanPage"),
);
const DigitalLivestockRecordsPage = lazy(
  () => import("./pages/guides/DigitalLivestockRecordsPage"),
);
const BreedsTeddaGulabiKamoriPage = lazy(
  () => import("./pages/guides/BreedsTeddaGulabiKamoriPage"),
);
const RegionDaurSindhPage = lazy(() => import("./pages/guides/RegionDaurSindhPage"));
const PakistanLivestockOverviewPage = lazy(
  () => import("./pages/guides/PakistanLivestockOverviewPage"),
);
const QurbaniGoatBuyingGuidePage = lazy(
  () => import("./pages/guides/QurbaniGoatBuyingGuidePage"),
);
const GoatVaccinationHealthPage = lazy(
  () => import("./pages/guides/GoatVaccinationHealthPage"),
);
const OnlineLivestockTrustPakistanPage = lazy(
  () => import("./pages/guides/OnlineLivestockTrustPakistanPage"),
);
const LiveTrackedGoatFarmPage = lazy(
  () => import("./pages/guides/LiveTrackedGoatFarmPage"),
);

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
    Loading…
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route path="/" element={<Index />} />
          <Route
            path="/auth"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Auth />
              </Suspense>
            }
          />
          <Route
            path="/login"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Auth />
              </Suspense>
            }
          />
          <Route
            path="/customer"
            element={
              <Suspense fallback={<RouteFallback />}>
                <CustomerPortal />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AdminWrapper />
              </Suspense>
            }
          />
          <Route
            path="/admin-old"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AdminDashboard />
              </Suspense>
            }
          />
          <Route
            path="/farmherd360"
            element={
              <Suspense fallback={<RouteFallback />}>
                <FarmHerd360Landing />
              </Suspense>
            }
          />
          <Route
            path="/pitch"
            element={
              <Suspense fallback={<RouteFallback />}>
                <PitchDeck />
              </Suspense>
            }
          />
          <Route
            path="/guides/goat-farming-pakistan"
            element={
              <Suspense fallback={<RouteFallback />}>
                <GoatFarmingPakistanPage />
              </Suspense>
            }
          />
          <Route
            path="/guides/digital-livestock-records-pakistan"
            element={
              <Suspense fallback={<RouteFallback />}>
                <DigitalLivestockRecordsPage />
              </Suspense>
            }
          />
          <Route
            path="/breeds/tedda-gulabi-kamori-sindh"
            element={
              <Suspense fallback={<RouteFallback />}>
                <BreedsTeddaGulabiKamoriPage />
              </Suspense>
            }
          />
          <Route
            path="/region/goat-farming-daur-sindh"
            element={
              <Suspense fallback={<RouteFallback />}>
                <RegionDaurSindhPage />
              </Suspense>
            }
          />
          <Route
            path="/guides/pakistan-livestock-goats-and-cattle"
            element={
              <Suspense fallback={<RouteFallback />}>
                <PakistanLivestockOverviewPage />
              </Suspense>
            }
          />
          <Route
            path="/guides/qurbani-goat-buying-guide-pakistan"
            element={
              <Suspense fallback={<RouteFallback />}>
                <QurbaniGoatBuyingGuidePage />
              </Suspense>
            }
          />
          <Route
            path="/guides/goat-vaccination-health-pakistan"
            element={
              <Suspense fallback={<RouteFallback />}>
                <GoatVaccinationHealthPage />
              </Suspense>
            }
          />
          <Route
            path="/guides/online-maweshi-khareedari-pakistan-amanat"
            element={
              <Suspense fallback={<RouteFallback />}>
                <OnlineLivestockTrustPakistanPage />
              </Suspense>
            }
          />
          <Route
            path="/guides/live-tracked-goat-farm-pakistan"
            element={
              <Suspense fallback={<RouteFallback />}>
                <LiveTrackedGoatFarmPage />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <NotFound />
              </Suspense>
            }
          />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
